import React from "react";
import PropTypes from "prop-types";
import { Table } from "reactstrap";
import { Loader } from "rsuite";
import PromotionsFilterBar from "./PromotionsFilterBar";
import PromotionRow from "./PromotionRow";
import PromotionsConfirmModals, {
  useConfirmModalsState,
} from "./PromotionsConfirmModals";
import TableFooter from "../../components/common/table/footer/TableFooter";
import { usePromotionsTableData } from "./usePromotionsTableData";
import { exportPromotions } from "./promotionsExport";
import {
  mapStatusFilterToApi,
  mapTypeFilterToApi,
  getStatusFilterValue,
  getTypeFilterValue,
} from "./promotionsTableUtils";
import sortUpDownIcon from "../../assets/img/sort-updown.svg";
import sortAscIcon from "../../assets/img/sort-asc.svg";
import sortDescIcon from "../../assets/img/sort-desc.svg";

const DEFAULT_PAGE_SIZE = 10;

function PromotionsTable({
  selectedProperty,
  onEdit,
  onView,
  onDisable,
  onActivate,
  onDelete,
  isGetPromosList,
  isPilotHotel = false,
}) {
  const modals = useConfirmModalsState();

  // Promotions data + query state
  const {
    queryParams,
    searchInputValue,
    handleFilterChange,
    handleSearchChange,
    handleSortChange,
    handlePageChange,
    promotions,
    pagable,
    loading,
  } = usePromotionsTableData({
    propertyId: selectedProperty?.propertyId,
    isGetPromosList,
  });

  // Map UI filter values to API values
  const handleStatusFilterChange = (value) => {
    handleFilterChange("status", mapStatusFilterToApi(value));
  };

  const handleTypeFilterChange = (value) => {
    handleFilterChange("promoType", mapTypeFilterToApi(value));
  };

  const handleSearch = (e) => {
    handleSearchChange(e.target.value);
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === "Enter") {
      handleSearchChange(e.target.value, true);
    }
  };

  const handleExport = () => {
    exportPromotions({ queryParams, propertyId: selectedProperty.propertyId });
  };

  const handleStatusToggle = (promo) => {
    if (
      promo.status === "Active" ||
      promo.status === "Inactive" ||
      promo.status === "Draft"
    ) {
      modals.open("status", promo, {
        action: promo.status === "Active" ? "disable" : "enable",
      });
    }
  };

  const handleDelete = (promo) => {
    modals.open("delete", promo);
  };

  // EO-1264: the deleted view is read-only end to end — no toggle, no name link, no
  // Edit/Delete. Driven off the query param rather than a per-row flag so the grid shape
  // is decided once.
  const isDeletedView = queryParams.status === "Deleted";

  // Render sort icon based on current sort state
  const renderSortIcon = (field) => {
    const isCurrentSortColumn = queryParams.sortField === field;

    let iconSrc;
    if (isCurrentSortColumn) {
      iconSrc =
        queryParams.sortDirection === "asc" ? sortAscIcon : sortDescIcon;
    } else {
      iconSrc = sortUpDownIcon;
    }

    return (
      <button
        type="button"
        className={`sort-btn ${isCurrentSortColumn ? "active" : ""}`}
        onClick={() => handleSortChange(field)}
        aria-label={
          isCurrentSortColumn ? `Sorted ${queryParams.sortDirection}` : "Sort"
        }
      >
        <img src={iconSrc} alt="" width="16" height="16" />
      </button>
    );
  };

  return (
    <div className="promo-table-container">
      <PromotionsFilterBar
        statusFilter={getStatusFilterValue(queryParams)}
        typeFilter={getTypeFilterValue(queryParams)}
        searchValue={searchInputValue}
        onStatusFilterChange={handleStatusFilterChange}
        onTypeFilterChange={handleTypeFilterChange}
        onSearch={handleSearch}
        onSearchKeyDown={handleSearchKeyDown}
        onExport={handleExport}
        loading={loading}
        isPilotHotel={isPilotHotel}
      />
      <div className={`table-scroll-wrapper ${loading ? "loading" : ""}`}>
        {loading && (
          <div className="table-loader-overlay">
            <Loader size="md" content="Loading..." />
          </div>
        )}
        <Table hover>
          <thead>
            <tr>
              <th className="sticky-col">Promo Name</th>
              <th>Adjustment</th>
              <th>
                Status
                {renderSortIcon("status")}
              </th>
              <th>
                Booking Window
                {renderSortIcon("bookingWindow")}
              </th>
              <th>
                Stay Dates
                {renderSortIcon("stayDates")}
              </th>
              <th>Rate Plans</th>
              <th>Exclusive Clients</th>
              <th>
                Created
                {renderSortIcon("created")}
              </th>
              <th>Revenue</th>
              {isDeletedView && (
                <>
                  <th>Deleted On</th>
                  <th>Deleted By</th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {promotions &&
              promotions.length > 0 &&
              promotions.map((promo, index) => (
                <PromotionRow
                  key={promo.id || index}
                  promo={promo}
                  index={index}
                  selectedProperty={selectedProperty}
                  isPilotHotel={isPilotHotel}
                  isDeletedView={isDeletedView}
                  onEdit={onEdit}
                  onView={onView ?? onEdit}
                  onStatusToggle={handleStatusToggle}
                  onDelete={handleDelete}
                />
              ))}
          </tbody>
        </Table>
        {!loading && !promotions?.length && (
          <div className="table-empty">No data found</div>
        )}
      </div>
      {pagable && pagable.totalPages > 1 && (
        <TableFooter
          total={pagable.totalElements}
          loading={loading}
          pageSize={DEFAULT_PAGE_SIZE}
          currentPage={pagable.pageNumber + 1}
          onPageChange={(page) => handlePageChange(page - 1)}
          maxButtons={3}
        />
      )}
      <PromotionsConfirmModals
        state={modals.state}
        onClose={modals.close}
        selectedProperty={selectedProperty}
        onDisable={onDisable}
        onActivate={onActivate}
        onDelete={onDelete}
      />
    </div>
  );
}

PromotionsTable.propTypes = {
  selectedProperty: PropTypes.shape({
    propertyId: PropTypes.string,
    acceptedCurrencies: PropTypes.arrayOf(PropTypes.string),
  }),
  onEdit: PropTypes.func,
  onView: PropTypes.func,
  onDisable: PropTypes.func,
  onActivate: PropTypes.func,
  onDelete: PropTypes.func,
  isGetPromosList: PropTypes.bool,
  isPilotHotel: PropTypes.bool,
};

export default PromotionsTable;
